import { useMemo, useState } from "react";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { fmtNum } from "./formatters";

/**
 * Full-width horizontal timeseries — yellow bars = cumplimiento PVA
 * (fallback grey if no target loaded), black line = Lead→Sale conversion.
 * Always-visible monthly callouts (units, leads, L2S%) below the axis so
 * the exec view doesn't require hovering.
 */
export default function TimeseriesChart({ ts, channelFilter }) {
  const [hover, setHover] = useState(null);
  const W = 1200, H = 400, left = 80, right = 24, top = 40, bottom = 156;
  const innerW = W - left - right;
  const innerH = H - top - bottom;
  const step = innerW / 12;
  const barW = Math.max(14, step - 24);

  const atnMax  = useMemo(() => ts.reduce((m, x) => Math.max(m, x.attainment_pct || 0), 0) || 100, [ts]);
  const convMax = useMemo(() => ts.reduce((m, x) => Math.max(m, x.conversion_pct  || 0), 0) || 1,   [ts]);
  // Separate scale for the Tasa de Calificación blue line so it renders
  // proportionally against its own peak instead of being flattened
  // against the (usually much smaller) L2S conversion peak.
  const qualMax = useMemo(() =>
    ts.reduce((m, x) => Math.max(m, x.qualification_rate || 0), 0) || 1, [ts]);
  // Fallback scale for months without a target — normalizes bar heights
  // against the largest sales_count in the series so users still see a
  // proportional bar (yellow if attainment known, gray if target missing).
  const salesMax = useMemo(() => ts.reduce((m, x) => Math.max(m, x.sales_count || 0), 0) || 1, [ts]);

  return (
    <div className="surface p-6" data-testid="scorecard-timeseries">
      <div className="mb-3 flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-base font-extrabold inline-flex items-center gap-1.5"
            style={{ letterSpacing: "-0.01em" }}>
            Evolución Mensual · KPIs Comerciales
            <ScorecardInfoTip label="Timeseries"
              formula="Barras = cumplimiento PVA (unidades TOTALES vendidas ÷ target × 100). Línea negra = Lead→Venta (unidades DIGITALES atribuidas ÷ leads × 100). Línea azul (punteada) = Tasa de Calificación (leads CALIFICADOS en CRM ÷ leads DIGITALES IDENTIFICADOS en CRM × 100)."
              extra="Las barras son globales (PVA es showroom-level). La línea negra muestra SOLO unidades atribuidas a leads digitales. La línea azul mide la calidad del lead: qué % de los que llegan al CRM logran ser Calificados por Sales. Las tres son medidas independientes."
              source="sales + sales_targets (barras) · leads_digital + sales (línea negra) · leads_digital + leads_raw.status=Calificado (línea azul)" />
          </h3>
          <p className="text-[11px] text-gray-500 mt-1">
            Cumplimiento PVA (barras amarillas · global · <strong>unidades totales</strong>) vs Conversión
            Lead-to-Sale (línea negra ·{" "}
            <span style={{ color: "#166534", fontWeight: 800 }}>solo unidades digitales</span>
            {channelFilter ? <> · <strong style={{ color: "#854d0e" }}>{channelFilter}</strong></> : null})
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-700">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "#f4c400", display: "inline-block" }} />
            Cumplimiento PVA (%)
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-500">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "#cbd5e1", display: "inline-block" }} />
            Sin meta cargada
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-700">
            <span style={{ width: 12, height: 3, background: "#111827", display: "inline-block", borderRadius: 1 }} />
            Conversión L2S (%)
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-700">
            <span style={{ width: 12, height: 3, background: "#2563eb", display: "inline-block", borderRadius: 1 }} />
            Tasa de Calificación (%)
          </span>
          {channelFilter && (
            <span className="pill text-[10px] font-extrabold uppercase tracking-wider"
              style={{ background: "#f4c400", color: "#111827", padding: "4px 8px" }}
              data-testid="timeseries-filter-badge">
              Línea filtrada · {channelFilter}
            </span>
          )}
        </div>
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "auto", display: "block" }}
          onMouseLeave={() => setHover(null)}
          data-testid="timeseries-svg">
          {/* Y-axis guides (attainment scale, left) */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <g key={t}>
              <line x1={left} x2={W - right}
                y1={top + innerH * (1 - t)} y2={top + innerH * (1 - t)}
                stroke={t === 0 ? "#cbd5e1" : "#f1f5f9"} strokeWidth="1" />
              <text x={left - 8} y={top + innerH * (1 - t) + 3} textAnchor="end"
                fontSize="10" fontWeight="700" fill="#94a3b8">
                {`${Math.round(atnMax * t)}%`}
              </text>
            </g>
          ))}

          {/* Bars: cumplimiento PVA (yellow) when target exists; otherwise
              fall back to a proportional gray bar based on sales_count so
              months without a target still show a visible column. */}
          {ts.map((m, i) => {
            const sc = m.sales_count || 0;
            if (m.attainment_pct == null && sc === 0) return null;
            const cx = left + i * step + step / 2;
            const hasTarget = m.attainment_pct != null;
            const h = hasTarget
              ? Math.max(2, (m.attainment_pct / atnMax) * innerH)
              : Math.max(2, (sc / salesMax) * innerH);
            const y = top + innerH - h;
            const isHover = hover && hover.month === m.month;
            const barColor = hasTarget ? "#f4c400" : "#cbd5e1";
            return (
              <g key={`atn-${m.month}`}>
                <rect x={left + i * step} y={0} width={step} height={H}
                  fill="transparent" style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover(m)} />
                <rect x={cx - barW / 2} y={y} width={barW} height={h} rx={3}
                  fill={barColor} opacity={isHover ? 1 : 0.85} pointerEvents="none" />
                <text x={cx} y={y - 6} textAnchor="middle"
                  fontSize="13" fontWeight="800" fill="#111827"
                  pointerEvents="none">
                  {`${fmtNum(sc)}u`}
                </text>
              </g>
            );
          })}

          {/* Line: conversion% (right scale) */}
          <polyline
            points={ts.filter((m) => m.conversion_pct != null).map((m) => {
              const i  = m.month - 1;
              const cx = left + i * step + step / 2;
              const y  = top + innerH - (m.conversion_pct / convMax) * innerH;
              return `${cx},${y}`;
            }).join(" ")}
            fill="none" stroke="#111827" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
          {ts.map((m) => {
            if (m.conversion_pct == null) return null;
            const i  = m.month - 1;
            const cx = left + i * step + step / 2;
            const y  = top + innerH - (m.conversion_pct / convMax) * innerH;
            return <circle key={`cd-${m.month}`} cx={cx} cy={y} r="4"
              fill="#111827" stroke="#fff" strokeWidth="1.5" pointerEvents="none" />;
          })}

          {/* Line: Tasa de Calificación (blue) — Calificados ÷ Identificados
              × 100 per month. Rendered against its own scale (qualMax) so
              the shape reads independently of the L2S conversion line. */}
          <polyline
            points={ts.filter((m) => m.qualification_rate != null).map((m) => {
              const i  = m.month - 1;
              const cx = left + i * step + step / 2;
              const y  = top + innerH - (m.qualification_rate / qualMax) * innerH;
              return `${cx},${y}`;
            }).join(" ")}
            fill="none" stroke="#2563eb" strokeWidth="2.4"
            strokeDasharray="4 3"
            strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
          {ts.map((m) => {
            if (m.qualification_rate == null) return null;
            const i  = m.month - 1;
            const cx = left + i * step + step / 2;
            const y  = top + innerH - (m.qualification_rate / qualMax) * innerH;
            return <circle key={`qd-${m.month}`} cx={cx} cy={y} r="4"
              fill="#2563eb" stroke="#fff" strokeWidth="1.5" pointerEvents="none" />;
          })}

          {/* X-axis: month label + always-on monthly callout stack */}
          {ts.map((m, i) => {
            const cx = left + i * step + step / 2;
            const isActive = hover && hover.month === m.month;
            const hasData = m.sales_count != null || m.leads != null;
            return (
              <g key={`lbl-${m.month}`}>
                <text x={cx} y={top + innerH + 18} textAnchor="middle"
                  fontSize="11" fontWeight={isActive ? 800 : 700}
                  fill={isActive ? "#111827" : "#64748b"}
                  style={{ fontFamily: "Manrope, sans-serif" }}>
                  {m.label}
                </text>
                {hasData && (
                  <g pointerEvents="none">
                    <text x={cx} y={top + innerH + 40} textAnchor="middle"
                      fontSize="14" fontWeight="800" fill="#111827">
                      {`${fmtNum(m.sales_count || 0)}u`}
                    </text>
                    <text x={cx} y={top + innerH + 58} textAnchor="middle"
                      fontSize="12" fontWeight="700" fill="#0ea5e9">
                      {`${fmtNum(m.leads || 0)} leads`}
                    </text>
                    {/* Digital units per month — the numerator of the L2S%
                        line. Shown in green so the eye connects it to the
                        black line above. */}
                    <text x={cx} y={top + innerH + 76} textAnchor="middle"
                      fontSize="12" fontWeight="800" fill="#166534">
                      {`${fmtNum(m.digital_units || 0)}u dig.`}
                    </text>
                    <text x={cx} y={top + innerH + 94} textAnchor="middle"
                      fontSize="12.5" fontWeight="800"
                      fill={(m.conversion_pct || 0) >= 2 ? "#166534" : "#854d0e"}>
                      {m.conversion_pct != null ? `${m.conversion_pct.toFixed(2)}%` : "—"}
                    </text>
                    {/* Tasa de Calificación (blue) — Calificados ÷
                        Identificados. Second identity line of the chart:
                        speaks to lead quality, not sale outcome. */}
                    <text x={cx} y={top + innerH + 112} textAnchor="middle"
                      fontSize="12.5" fontWeight="800"
                      fill={(m.qualification_rate || 0) >= 5 ? "#1e40af" : "#64748b"}>
                      {m.qualification_rate != null ? `${m.qualification_rate.toFixed(2)}%` : "—"}
                    </text>
                    <text x={cx} y={top + innerH + 130} textAnchor="middle"
                      fontSize="11" fontWeight="700" fill="#64748b">
                      {m.identified_count != null
                        ? `${fmtNum(m.qualified_count || 0)}/${fmtNum(m.identified_count)}`
                        : "—"}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Column-header labels for the callout stack (tiny hint) */}
          <text x={left - 8} y={top + innerH + 40} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#94a3b8" style={{ letterSpacing: "0.06em" }}>UNIDADES</text>
          <text x={left - 8} y={top + innerH + 58} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#94a3b8" style={{ letterSpacing: "0.06em" }}>LEADS</text>
          <text x={left - 8} y={top + innerH + 76} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#166534" style={{ letterSpacing: "0.06em" }}>DIGITALES</text>
          <text x={left - 8} y={top + innerH + 94} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#94a3b8" style={{ letterSpacing: "0.06em" }}>L2S%</text>
          <text x={left - 8} y={top + innerH + 112} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#2563eb" style={{ letterSpacing: "0.06em" }}>CALIF%</text>
          <text x={left - 8} y={top + innerH + 130} textAnchor="end" fontSize="10"
            fontWeight="800" fill="#94a3b8" style={{ letterSpacing: "0.06em" }}>C/ID</text>
        </svg>

        {hover && (
          <div className="lp2-hourly-callout" data-testid="scorecard-ts-callout">
            <span className="lp2-hourly-callout__hour">{hover.label} {(new Date()).getFullYear()}</span>
            <span className="lp2-hourly-callout__count">
              PVA {hover.attainment_pct != null ? `${hover.attainment_pct.toFixed(1)}%` : "—"}
            </span>
            <span className="lp2-hourly-callout__pct">
              Conv {hover.conversion_pct != null ? `${hover.conversion_pct.toFixed(2)}%` : "—"}
            </span>
            <span className="lp2-hourly-callout__pct" style={{ color: "#2563eb" }}>
              Calif {hover.qualification_rate != null ? `${hover.qualification_rate.toFixed(2)}%` : "—"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
